/*
 * A Redux middleware that interprets FETCH_REQUEST_WITH_TYPES actions.
 * Performs the call and promises to dispatch three actions:
 * A "Request" action, a "Success" action, and an "Errored" action.
 */

import {
	FETCH_REQUEST_WITH_TYPES,
	REFRESH_TOKEN_REQUEST,
	refreshTokenRequest,
	setIdempotencyKey,
} from '../actions/api/index.js';
import { logOut } from '../actions/session';
import { selectIsAuthenticated } from '../selectors/session';
import { isProdOrStaging } from 'stash/utils';
import { getCookie } from 'stash/utils/cookies';

import { getErrorMessage } from 'stash/utils/errors';
import {
	middlewareRequestErrorContext,
	middlewareRequestErrorFingerprint,
	middlewareRequestErrorMessage,
} from './requestErrorReport';
import {
	ACCESS_TOKEN_COOKIE_NAME,
	REFRESH_TOKEN_COOKIE_NAME,
	UUID_COOKIE_NAME,
} from '../constants/session';
import { v4 as uuidv4 } from 'uuid';
import hash from 'stable-hash';
import { datadogRum } from '@datadog/browser-rum';

const FETCH_MW_KEY = '__fetchMiddleware';
export const MOCK_MODE = 'mockMode';
export const CORS_MODE = 'cors';

export class MiddlewareRequestError extends Error {
	constructor(message, details = {}) {
		super(message);
		this.name = 'MiddlewareRequestError';
		Object.assign(this, details);
	}
}

export const REFRESH_TOKEN_EXPIRED = Symbol('REFRESH_TOKEN_EXPIRED');

const generateIdempotencyKey = () => {
	return uuidv4();
};

const getIdempotencyKey = (state, action, dispatch) => {
	const hashKey = hash({
		path: action.request.path,
		method: action.request.method,
		body: action.request.body,
	});
	if (
		state.application.idempotencyKey[hashKey] &&
		state.application.idempotencyKey[hashKey].timestamp > Date.now() - 1000 * 60 // 1 minute
	) {
		datadogRum.addAction('Possible Duplicate Request', {
			path: action.request.path,
			method: action.request.method,
			body: action.request.body,
			idempotencyKey: state.application.idempotencyKey[hashKey].idempotencyKey,
			timestamp: state.application.idempotencyKey[hashKey].timestamp,
		});
		return state.application.idempotencyKey[hashKey].idempotencyKey;
	} else {
		const newIdempotencyKey = generateIdempotencyKey();
		dispatch(
			setIdempotencyKey(hashKey, {
				idempotencyKey: newIdempotencyKey,
				timestamp: Date.now(),
			})
		);
		return newIdempotencyKey;
	}
};

// Validate that the FETCH_REQUEST_WITH_TYPES action has everything
// it needs to actually make the call and dispatch the three actions.

const validateFetch = ({ request, types }) => {
	if (!types || types.length !== 3) throw new Error('Fetch: Expected three action types');
	if (!request) throw new Error('Fetch: No request key specified.');
	if (!request.method) throw new Error('Fetch: No HTTP method specified.');
	if (!request.path) throw new Error('Fetch: No URL path specified.');
};

// The three actions which make up an async request (request, success, errored)
// are unpacked in the middleware and dispatched to Redux from here.

const dispatchFetchAction = (dispatch, type, { ...action }, response) => {
	return dispatch({
		...action,
		type,
		response,
	});
};

// Build the URL for the request. Allow for common params to be injected from
// state (such as :user_id)

export const getRequestURL = (apiKey, path) => {
	let newPath = path;

	if (newPath.includes(':uuid')) {
		const uuid = apiKey && apiKey.uuid ? apiKey.uuid : getCookie(UUID_COOKIE_NAME);
		if (Boolean(uuid)) {
			newPath = newPath.replace(':uuid', uuid);
		} else {
			return null;
		}
	}

	return newPath;
};

/**
 * Parse response body as JSON. Empty or non-JSON bodies return a structured
 * fallback so the middleware never throws "Unexpected end of JSON input".
 */
const parseResponseBody = (res, text) => {
	const trimmed = text != null ? text.trim() : '';
	if (trimmed === '') {
		return { status: res.status, message: 'API returned a server error' };
	}
	try {
		const json = JSON.parse(text);
		return json;
	} catch (_) {
		return {
			status: res.status,
			message: 'API returned a server error',
			description: 'Response was not valid JSON',
		};
	}
};

export const handleFetchRequest = (url, options, action) => {
	if (options.mode === MOCK_MODE) {
		return Promise.resolve(action.mockResponse);
	}

	return fetch(url, options).then((res) => {
		if (res.status === 204) return { success: true, status: 204 };
		return res.text().then((text) => {
			const json = parseResponseBody(res, text);
			// some api responses include a status field distinct from the http status code
			const originalStatus = json.status != null ? { originalStatus: json.status } : {};

			if (json instanceof Array || Array.isArray(json)) {
				return { body: json, status: res.status, ...originalStatus };
			}
			return { ...json, status: res.status, ...originalStatus };
		});
	});
};

export const sendRequest = ({
	url,
	options,
	dispatch,
	action,
	state,
	getState: _getState,
}) => {
	// Make the HTTP request. A Promise is returned so that dispatch
	// is 'thennable'
	return handleFetchRequest(url, options, action)
		.then((json) => {
			if (json.status >= 400) throw json;

			// Dispatch the "Success" action
			dispatchFetchAction(
				dispatch,
				action[FETCH_MW_KEY].successType,
				{ ...action, [FETCH_MW_KEY]: undefined },
				json
			);

			return Promise.resolve(json);
		})
		.catch((json) => {
			dispatchFetchAction(
				dispatch,
				action[FETCH_MW_KEY].erroredType,
				{ ...action, [FETCH_MW_KEY]: undefined },
				json
			);

			// If the refresh token is expired, log out
			if (
				(json.status === 400 || json.status === 401) &&
				action.type === REFRESH_TOKEN_REQUEST
			) {
				dispatch(logOut());
				return Promise.resolve(REFRESH_TOKEN_EXPIRED);
			}

			// If we ever get a 401 from the API, refresh the token under auth v2, log the user out under legacy auth.
			// Skip when it's the refresh request itself that failed (handled above).
			// Auth can diverge: AUTHORIZATION comes from the cookie at send time, while Redux may already be cleared.
			if (json.status === 401 && action.type !== REFRESH_TOKEN_REQUEST) {
				const token = getCookie(REFRESH_TOKEN_COOKIE_NAME);
				if (token) {
					// Wait for refresh (in progress or new) to complete before retrying.
					// refreshTokenRequest returns the in-flight promise when a refresh is already pending.
					// Return the chain so callers await full completion (refresh + retry) and avoid race conditions.
					return dispatch(refreshTokenRequest(token))
						.then((result) => {
							if (result === REFRESH_TOKEN_EXPIRED) {
								return Promise.resolve();
							}
							return dispatch(action);
						})
						.catch((error) => {
							if (process.env.NODE_ENV !== 'production') {
								console.error('Token refresh failed:', error);
							}
							throw error;
						});
				}

				// Cookie-less decodeURIComponent(undefined) yields the string "undefined" on AUTHORIZATION.
				const authHeader = options?.headers?.AUTHORIZATION;
				const sentAuthHeader =
					typeof authHeader === 'string' &&
					authHeader.length > 0 &&
					authHeader !== 'undefined';
				const requestHadAuth = selectIsAuthenticated(state) || sentAuthHeader;
				if (requestHadAuth) {
					const currentState = _getState?.() ?? state;
					// Logout race: session already cleared — don't throw MiddlewareRequestError
					if (!selectIsAuthenticated(currentState)) {
						return Promise.resolve();
					}
					dispatch(logOut());
					return Promise.resolve();
				}
			}

			const stringifiedErrorMsg = middlewareRequestErrorMessage(json);
			const requestContext = middlewareRequestErrorContext({
				actionType: action.type,
				status: json?.status,
				method: action.request?.method,
				path: action.request?.path,
			});

			const err = new MiddlewareRequestError(stringifiedErrorMsg, {
				...json,
				displayError: getErrorMessage(json),
				customDeveloperMessage: 'Middleware Request Failed',
				url,
				stringifiedErrorMessage: stringifiedErrorMsg,
				dd_fingerprint: middlewareRequestErrorFingerprint(requestContext),
				dd_context: requestContext,
			});

			throw err;
		});
};

// The actual middleware. Any action with type FETCH_REQUEST_WITH_TYPES
// will trigger this middleware. The HTTP call is made and the three
// actions are unpacked and promised.

const fetchMiddleware =
	({ dispatch, getState }) =>
	(next) =>
	(action) => {
		if (action.type === FETCH_REQUEST_WITH_TYPES) {
			// Validate the fetch action
			validateFetch(action);

			// Get the specified action types
			const [requestType, successType, erroredType] = action.types;

			// Dispatch the "Request" action immediately.
			return dispatchFetchAction(dispatch, requestType, {
				...action,
				[FETCH_MW_KEY]: { successType, erroredType },
			});
		}

		if (action[FETCH_MW_KEY]) {
			// Prepare headers for the network request
			const state = getState();
			const apiKey = state.entities && state.entities.api_key;
			const basePath = action.request.basePath || window.Stash.CUSTOM_API_PATH || '';
			const path = getRequestURL(apiKey, action.request.path);
			if (!path) {
				dispatch(logOut());
				return Promise.resolve();
			}
			const url = `${basePath}${path}`;
			let accessToken =
				action.type !== REFRESH_TOKEN_REQUEST &&
				decodeURIComponent(getCookie(ACCESS_TOKEN_COOKIE_NAME));
			const mode = !isProdOrStaging() && action.mockResponse ? MOCK_MODE : CORS_MODE;

			const options = {
				mode,
				body:
					action.request.method !== 'GET'
						? JSON.stringify(action.request.body)
						: undefined,
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
					/**
					 * If the action is a refresh token request, do not include the access token
					 * or smaug will throw a 401 error if the token is expired.
					 */
					AUTHORIZATION:
						action.type === REFRESH_TOKEN_REQUEST ? undefined : accessToken || undefined,
					...action.request.headers,
				},
				method: action.request.method,
				...action.request.options,
			};

			if (action.passIdempotencyKey) {
				options.headers['Idempotency-Key'] = getIdempotencyKey(state, action, dispatch);
			}

			// Continue dispatching the request action
			next({ ...action, [FETCH_MW_KEY]: undefined });

			return sendRequest({ url, options, dispatch, action, state, getState });
		}

		return next(action);
	};

export default fetchMiddleware;
